import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { adminAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import Spinner from '../components/Spinner'
import DiagnosisPanel from '../components/DiagnosisPanel'
import ShadowOutcomesPanel from '../components/ShadowOutcomesPanel'
import Phase2ModelPanel from '../components/Phase2ModelPanel'
import ScheduledTasksPanel from '../components/ScheduledTasksPanel'
import CapabilitiesPanel from '../components/CapabilitiesPanel'
import OutcomeAuditPanel from '../components/OutcomeAuditPanel'
import EnginePausePanel from '../components/EnginePausePanel'
import EngineEventsStrip from '../components/EngineEventsStrip'
import { stopTooTightLabel, useEngineRules } from '../hooks/useEngineRules'
import { apiErrorMessage } from '../utils/apiError'
import type { CounterWindow, DiagnosticsFacts, OutcomeCount } from '../types/adminDiagnostics'

/**
 * Admin-only engine diagnostics, stage 1: the raw-facts panel.
 *
 * Everything shown is a database or Redis read of state the engine already
 * keeps -- the losing streak, stop-outs, the scan heartbeat and timeline, and
 * the per-gate outcome and failure counters. The alerts are fixed rules over
 * those facts. There is no AI on this page and it changes nothing. The route
 * is gated to superusers in App.tsx and linked only from the admin Engine page.
 *
 * The facts endpoint also returns the record, the open book and Phase 2
 * progress. They are not shown here because the Dashboard already shows them;
 * they stay in the response for the stage-2 diagnosis, which reads it whole.
 *
 * Below the facts sits the stage-2 diagnosis (components/DiagnosisPanel),
 * the one part of the page that calls an AI model, on demand only.
 */

const REFRESH_MS = 60_000

/** Scan outcomes in the order the engine reaches them. */
const OUTCOME_ORDER: { key: string; label: string }[] = [
  { key: 'signal_created', label: 'Signal created' },
  { key: 'liquidity_floor', label: 'Liquidity floor' },
  { key: 'phase1_filter', label: 'Phase 1 quality filter' },
  { key: 'phase1_score', label: 'Phase 1 score' },
  { key: 'phase2', label: 'Phase 2 (ML)' },
  { key: 'phase3', label: 'Phase 3 (ensemble)' },
  { key: 'confidence_floor', label: 'Confidence floor' },
  { key: 'short_regime_block', label: 'Short regime block' },
  { key: 'short_confidence_floor', label: 'Short confidence floor' },
  { key: 'regime_uncertainty', label: 'Regime uncertainty' },
  { key: 'cost_floor', label: 'Stop too tight' }, // labelled with its number in OutcomeTable
  { key: 'stop_too_wide', label: 'Stop too wide' },
  { key: 'net_ev', label: 'Net EV after costs' },
  { key: 'htf_disagree', label: 'Higher timeframe disagrees' },
  { key: 'btc_downtrend', label: 'BTC downtrend (longs, shadow only)' },
  { key: 'extreme_greed', label: 'Fear & Greed above ceiling (longs, shadow only)' },
  { key: 'short_paused', label: 'Shorts paused (shadow only)' },
  { key: 'direction_cap', label: 'Direction cap (shadow only)' },
  { key: 'stopout_cooldown', label: 'Cooldown after a stop-out (shadow only)' },
  { key: 'engine_paused', label: 'Engine paused (shadow only)' },
  { key: 'already_open', label: 'Skipped: setup already open' },
  { key: 'candle_taken', label: 'Skipped: candle already taken' },
  { key: 'entry_drifted', label: 'Skipped: entry drifted' },
  { key: 'persist_busy', label: 'Skipped: another signal being written' },
  { key: 'portfolio_heat_cap', label: 'Risk gate: heat cap' },
  { key: 'max_open_platform_signals', label: 'Risk gate: max open' },
  { key: 'excluded_config', label: 'Excluded by config' },
  { key: 'failed', label: 'Analysis failed' },
  { key: 'unlabelled_rejection', label: 'Unlabelled rejection' },
]

const FAILURE_LABELS: Record<string, string> = {
  scan_market_data: 'Scan: market data unavailable',
  scan_error: 'Scan: analysis error',
  scan_dispatch: 'Scan: dispatch failed',
  monitor_markets: 'Monitor: market list fetch failed',
  monitor_prices: 'Monitor: price fetch failed',
  monitor_update: 'Monitor: signal update failed',
  monitor_candles: 'Monitor: 1-minute candles failed (priced at the tick)',
  monitor_crash: 'Monitor: sweep crashed',
  paper_run: 'Paper engine: run failed',
  ai_credit: 'AI provider: credit balance too low',
  ai_auth: 'AI provider: API key rejected',
  ai_provider: 'AI provider: call failed',
}

const fmtR = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`

const fmtPct = (v: number | null | undefined, digits = 1) =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}%`

const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—')

const minutesSince = (iso: string | null) =>
  iso ? Math.round((Date.now() - new Date(iso).getTime()) / 60_000) : null

const fmtHeld = (minutes: number | null) => {
  if (minutes === null) return '—'
  if (minutes < 60) return `${minutes} min`
  const hours = minutes / 60
  return hours < 48 ? `${hours.toFixed(1)} h` : `${(hours / 24).toFixed(1)} d`
}

const Tile = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
  </div>
)

const OutcomeTable = ({ window, costFloorLabel }: { window: CounterWindow; costFloorLabel: string }) => {
  if (window.outcomes === null) {
    return <p className="p-4 text-sm text-gray-500">Counters unavailable: Redis could not be read.</p>
  }
  const outcomes = window.outcomes
  const known = new Set(OUTCOME_ORDER.map((o) => o.key))
  const rows = [
    ...OUTCOME_ORDER.filter((o) => outcomes[o.key]).map((o) =>
      o.key === 'cost_floor' ? { ...o, label: costFloorLabel } : o
    ),
    ...Object.keys(outcomes)
      .filter((k) => !known.has(k))
      .map((k) => ({ key: k, label: k })),
  ]
  const analysed = Object.values(outcomes).reduce((sum, c) => sum + c.total, 0)
  if (rows.length === 0) {
    return <p className="p-4 text-sm text-gray-500">No analyses counted in this window.</p>
  }
  return (
    <table className="min-w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
        <tr>
          <th className="px-4 py-2">Outcome</th>
          <th className="px-2 py-2 text-right">Long</th>
          <th className="px-2 py-2 text-right">Short</th>
          <th className="px-2 py-2 text-right">No direction</th>
          <th className="px-2 py-2 text-right">Total</th>
          <th className="px-2 py-2 text-right">Share</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map(({ key, label }) => {
          const c: OutcomeCount = outcomes[key]
          return (
            <tr key={key} className="text-gray-700" data-testid={`outcome-${key}`}>
              <td className="px-4 py-2 font-medium text-gray-900">{label}</td>
              <td className="px-2 py-2 text-right tabular-nums">{c.long}</td>
              <td className="px-2 py-2 text-right tabular-nums">{c.short}</td>
              <td className="px-2 py-2 text-right tabular-nums">{c['-']}</td>
              <td className="px-2 py-2 text-right tabular-nums">{c.total}</td>
              <td className="px-2 py-2 text-right tabular-nums">
                {analysed ? fmtPct((c.total / analysed) * 100) : '—'}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

const AdminDiagnostics = () => {
  const { user } = useAuthStore()
  const [facts, setFacts] = useState<DiagnosticsFacts | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [windowKey, setWindowKey] = useState<'24h' | '168h'>('24h')
  const engineRules = useEngineRules()

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setFacts(await adminAPI.diagnosticsFacts())
    } catch (err) {
      setError(apiErrorMessage(err, 'The diagnostics could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user?.is_superuser) return
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => clearInterval(timer)
  }, [user?.is_superuser, load])

  if (!user?.is_superuser) {
    return (
      <div className="max-w-md mx-auto text-center py-16">
        <ShieldExclamationIcon className="h-10 w-10 text-danger-500 mx-auto mb-3" />
        <h1 className="text-lg font-semibold text-gray-900">Access denied</h1>
        <p className="text-sm text-gray-500 mt-1">You don&apos;t have permission to view this page.</p>
      </div>
    )
  }

  const counterWindow = facts?.counters[windowKey]
  const failures = counterWindow?.failures
  const sinceScan = minutesSince(facts?.scan.last_scan_at ?? null)

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <Link to="/settings/engine" className="inline-flex items-center gap-1 text-sm text-accent hover:underline">
          <ChevronLeftIcon className="h-4 w-4" /> Engine
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-gray-900">Engine diagnostics</h1>
        <p className="mt-1 text-sm text-gray-500">
          What the engine is doing, from the data it already keeps. The facts use no AI; nothing on this page
          changes the engine. Facts refresh every minute; the diagnosis below runs only when asked.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="px-4 py-2 bg-accent text-terminal-bg font-semibold rounded-md hover:bg-accent-hover disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Spinner size="sm" />}
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {facts && <p className="text-xs text-gray-500">As of {fmtTime(facts.as_of)}</p>}
        </div>
      </div>

      <EngineEventsStrip />

      {error && (
        <div className="rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700" role="alert">
          {error}
        </div>
      )}

      {loading && !facts && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner size="sm" /> Loading…
        </div>
      )}

      {facts && (
        <>
          {facts.alerts.length > 0 && (
            <section className="space-y-2" aria-label="Alerts">
              {facts.alerts.map((alert) => (
                <div
                  key={alert.code}
                  role="alert"
                  data-testid={`alert-${alert.code}`}
                  className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
                    alert.severity === 'critical'
                      ? 'border-danger-300 bg-danger-50 text-danger-700'
                      : 'border-amber-300 bg-amber-50 text-amber-900'
                  }`}
                >
                  {alert.severity === 'critical' ? (
                    <XCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  )}
                  <p>{alert.message}</p>
                </div>
              ))}
            </section>
          )}

          <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label="At a glance">
            <Tile
              label="Losing streak"
              value={`${facts.streak.consecutive_losses} in a row`}
              sub={`${facts.streak.losses_today} losses today · limits ${facts.streak.max_consecutive_losses} in a row, ${facts.streak.max_daily_losses} a day`}
            />
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label="Last scan">
            <h2 className="text-lg font-semibold text-gray-900">Last scan</h2>
            <p className="mt-1 text-gray-600">
              {facts.scan.last_scan_at
                ? `${fmtTime(facts.scan.last_scan_at)} (${sinceScan} min ago) · ${facts.scan.last_outcome ?? '—'} · ${
                    facts.scan.symbols_scanned ?? '—'
                  } symbols`
                : 'No scan recorded.'}
            </p>
            <p className="mt-1 text-gray-600" data-testid="deploy-commits">
              Scanner commit <code>{facts.scan.scanner_commit ?? 'unknown'}</code> · API commit{' '}
              <code>{facts.scan.api_commit ?? 'unknown'}</code>
            </p>
            {facts.scan.scanner_commit && facts.scan.api_commit && facts.scan.scanner_commit !== facts.scan.api_commit && (
              <p className="mt-1 font-medium text-amber-700" data-testid="deploy-commit-mismatch">
                The scanner and the API run different commits: one of them has not been redeployed.
              </p>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-white" aria-label="Scan outcomes by gate">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 p-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Scan outcomes by gate</h2>
                <p className="text-sm text-gray-500">
                  One outcome per analysed symbol and timeframe: the signal it created, or the gate or skip that
                  stopped it. Direction is the one the analysis had reached.
                </p>
              </div>
              <div className="flex gap-1" role="group" aria-label="Counter window">
                {(['24h', '168h'] as const).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setWindowKey(key)}
                    aria-pressed={windowKey === key}
                    className={`rounded-md px-3 py-1 text-sm ${
                      windowKey === key ? 'bg-accent text-terminal-bg font-semibold' : 'border border-gray-300 text-gray-700'
                    }`}
                  >
                    {key === '24h' ? '24 hours' : '7 days'}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              {counterWindow && (
                <OutcomeTable window={counterWindow} costFloorLabel={stopTooTightLabel(engineRules)} />
              )}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm" aria-label="Failures">
            <h2 className="text-lg font-semibold text-gray-900">
              Failures ({windowKey === '24h' ? '24 hours' : '7 days'})
            </h2>
            {failures === null || failures === undefined ? (
              <p className="mt-1 text-gray-500">Counters unavailable: Redis could not be read.</p>
            ) : Object.keys(failures).length === 0 ? (
              <p className="mt-1 text-gray-500">None counted.</p>
            ) : (
              <ul className="mt-2 space-y-1">
                {Object.entries(failures)
                  .sort((a, b) => b[1] - a[1])
                  .map(([kind, n]) => (
                    <li key={kind} className="flex justify-between gap-4 text-gray-700" data-testid={`failure-${kind}`}>
                      <span>{FAILURE_LABELS[kind] ?? kind}</span>
                      <span className="tabular-nums">{n}</span>
                    </li>
                  ))}
              </ul>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-white" aria-label="Recent stop-outs">
            <div className="border-b border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900">Stop-outs, last 7 days</h2>
              <p className="text-sm text-gray-500">Held time is from signal creation to the stop.</p>
              {facts.stop_outs.length > 0 && facts.stop_outs.some((s) => s.reached_1r !== undefined) && (
                <p className="mt-1 text-sm text-gray-700" data-testid="stop-outs-reached-1r">
                  {facts.stop_outs.filter((s) => s.reached_1r).length} of {facts.stop_outs.length} reached +1R
                  before the stop.
                </p>
              )}
            </div>
            {facts.stop_outs.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">None.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2">Signal</th>
                      <th className="px-2 py-2">Closed</th>
                      <th className="px-2 py-2 text-right">Held</th>
                      <th className="px-2 py-2 text-right">Candles</th>
                      <th className="px-2 py-2 text-right">R</th>
                      <th className="px-2 py-2 text-right">Reached +1R first</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {facts.stop_outs.map((s) => (
                      <tr key={s.signal_id} className="text-gray-700">
                        <td className="px-4 py-2">
                          <span className="font-medium text-gray-900">
                            #{s.signal_id} {s.symbol} {s.signal_type} {s.timeframe}
                          </span>
                          {s.excluded_from_record && (
                            <span className="ml-2 text-xs text-gray-500">excluded from record</span>
                          )}
                        </td>
                        <td className="px-2 py-2">{fmtTime(s.closed_at)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtHeld(s.held_minutes)}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{s.candles_held ?? '—'}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{fmtR(s.r_multiple)}</td>
                        <td className="px-2 py-2 text-right">
                          {s.reached_1r === undefined ? '—' : s.reached_1r ? 'Yes' : 'No'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-white" aria-label="Scan timeline">
            <div className="border-b border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900">Scan and close timeline</h2>
            </div>
            {facts.scan.timeline.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">Nothing recorded.</p>
            ) : (
              <ul className="divide-y divide-gray-100 text-sm">
                {facts.scan.timeline.map((e, i) => (
                  <li key={`${e.kind}-${e.at}-${i}`} className="flex flex-wrap justify-between gap-2 px-4 py-2 text-gray-700">
                    <span>
                      {e.kind === 'scan'
                        ? `Scan · ${e.outcome ?? '—'}${e.new_signals !== null ? ` · ${e.new_signals} new` : ''}`
                        : `Close · #${e.signal_id} ${e.symbol} ${e.signal_type} · ${e.outcome} ${fmtR(e.r_multiple)}${
                            e.excluded_from_record ? ' (excluded)' : ''
                          }`}
                    </span>
                    <span className="text-gray-500">{fmtTime(e.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <EnginePausePanel />

      <CapabilitiesPanel />

      <ScheduledTasksPanel />

      <OutcomeAuditPanel />

      <ShadowOutcomesPanel />

      <Phase2ModelPanel />

      <DiagnosisPanel />
    </div>
  )
}

export default AdminDiagnostics
